// Renders an EDC 2026 wallpaper as an absolutely-positioned DOM tree at the
// device's native canvas resolution. Caller is responsible for putting it in
// an offscreen container, awaiting fonts, calling applyAutofit, and exporting.

import type { FestSet } from "../data/schedule";
import { setsForDayOnStages } from "../data/schedule";
import { STAGE_HEADER_WORDS, EDC_MAGENTA, EDC_CYAN, EDC_YELLOW, FAVORITE_GOLD } from "../data/stages";
import type { DeviceProfile } from "../data/devices";
import { fmtAxisHour, fmtBlockTime, DAY_LABELS, DAY_DATES } from "../lib/time";

const EDC_LOGO_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 175 60" preserveAspectRatio="xMidYMid meet"><path fill="currentColor" d="M50 34.6C49.3 38.5 45.7 41.5 41.7 41.5 33.9 41.5 30.2 31.4 36.3 26.5 42.3 21.6 51.5 27 50 34.6M17.1 24.5C19.5 24.5 21.7 25.6 23.3 27.2L8.8 31C9.8 27.2 13.2 24.5 17.1 24.5M68.4 41.5C63.7 41.5 59.9 37.7 59.9 33 59.9 28.3 63.7 24.5 68.4 24.5 71.4 24.5 74.1 26.1 75.6 28.4L84.9 28.4C82.8 21.3 76.2 16.1 68.4 16.1 64.8 16.1 61.5 17.2 58.7 19.1L58.7 5 50.2 9.4 50.2 18.3C43.4 14.4 34.7 15.8 29.4 21.3 26.2 17.9 21.7 16 17.1 16 7.6 16.1 0 23.7 0 33 0 42.4 7.6 50 17.1 50 20.9 50 24.4 48.7 27.3 46.6L22 39.9C20.6 41 18.8 41.5 17.1 41.5 14.8 41.5 12.6 40.6 11 38.9L24.8 35.3C25.4 39.4 27.3 43.2 30.6 45.9 36.6 51 45.7 51.3 52 46.6 53.1 45.7 54.2 44.7 55 43.6 58.3 47.6 63.2 50 68.4 50 76.2 50 82.8 44.8 84.9 37.6L75.6 37.6C74 40 71.3 41.5 68.4 41.5"/></svg>`;

const FIREWORKS_START_MIN = 6 * 60 + 42; // 1:42 AM measured from 7 PM grid origin
const FIREWORKS_END_MIN = 6 * 60 + 47;
const GRID_END_MIN = 630; // 7pm → 5:30am

export type WallpaperOpts = {
  day: 1 | 2 | 3;
  stages: string[];
  faveIds: ReadonlySet<string>;
  device: DeviceProfile;
  spotlightFaves: boolean;
  allSets: FestSet[];
};

export function renderWallpaper(opts: WallpaperOpts): HTMLElement {
  const { day, stages, faveIds, device, spotlightFaves, allSets } = opts;
  const W = device.w;
  const H = device.h;

  // Layout proportions scale with canvas. The 1320×2868 numbers we tuned on
  // for the iPhone 17 PM are the reference; we scale Y positions by H/2868.
  const sY = (n: number) => n * (H / 2868);
  const sX = (n: number) => n * (W / 1320);

  const TOP_LOGO = sY(100);
  const DAY_TITLE_Y = sY(570);
  const GRID_TOP = sY(960);
  const GRID_BOTTOM = sY(2580);
  const STAGE_HEAD_HEIGHT = sY(110);
  const LEFT_MARGIN = sX(96);
  const RIGHT_MARGIN = sX(24);
  const COL_GAP = sX(4);
  const TAGLINE_Y = sY(2780);

  const gridH = GRID_BOTTOM - GRID_TOP;
  const pxPerMin = gridH / GRID_END_MIN;
  const gridInnerW = W - LEFT_MARGIN - RIGHT_MARGIN;
  const colW = (gridInnerW - (stages.length - 1) * COL_GAP) / Math.max(stages.length, 1);

  const root = document.createElement("div");
  root.className = "wp-root";
  root.style.cssText = `width:${W}px; height:${H}px; background:#000; position:relative; overflow:hidden; font-family:Inter, sans-serif; color:#fff;`;
  root.dataset.deviceId = device.id;

  // Sunset-glow radial behind the day title
  const glow = document.createElement("div");
  glow.style.cssText = `
    position:absolute; top:${sY(-200)}px; left:50%;
    width:${sX(1800)}px; height:${sY(1100)}px;
    transform:translateX(-50%);
    background:radial-gradient(ellipse at center, ${EDC_MAGENTA}33 0%, ${EDC_MAGENTA}18 18%, transparent 55%);
    pointer-events:none;
  `;
  root.appendChild(glow);

  // Sunburst rays
  const sun = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  sun.setAttribute("viewBox", "-100 -100 200 200");
  sun.setAttribute("class", "sunburst");
  sun.style.cssText = `position:absolute; left:50%; top:${sY(360)}px; width:${sX(1400)}px; height:${sX(1400)}px; transform:translate(-50%, -50%); opacity:0.18; pointer-events:none;`;
  sun.innerHTML = `
    <defs>
      <radialGradient id="rayGrad">
        <stop offset="0%" stop-color="${EDC_MAGENTA}" stop-opacity="0"/>
        <stop offset="60%" stop-color="${EDC_MAGENTA}" stop-opacity="0.6"/>
        <stop offset="100%" stop-color="${EDC_MAGENTA}" stop-opacity="0"/>
      </radialGradient>
    </defs>
  `;
  for (let i = 0; i < 48; i++) {
    const reach = i % 2 === 0 ? -95 : -75;
    const ray = document.createElementNS("http://www.w3.org/2000/svg", "polygon");
    ray.setAttribute("points", `0,0 -1.4,${reach} 1.4,${reach}`);
    ray.setAttribute("fill", "url(#rayGrad)");
    ray.setAttribute("transform", `rotate(${i * 7.5})`);
    sun.appendChild(ray);
  }
  root.appendChild(sun);

  // Top: official EDC SVG wordmark + tagline
  const topMark = document.createElement("div");
  topMark.style.cssText = `position:absolute; top:${TOP_LOGO}px; left:0; width:100%; text-align:center; color:#fff;`;
  topMark.innerHTML = `
    <div style="display:inline-block; width:90px; opacity:0.9;">${EDC_LOGO_SVG}</div>
    <div style="font-family:Orbitron,sans-serif; font-size:11px; letter-spacing:0.6em; color:#555; margin-top:6px; font-weight:700;">LAS VEGAS · MAY 15-17 · 2026</div>
  `;
  root.appendChild(topMark);

  // Day-title hero block
  const dayTitle = document.createElement("div");
  dayTitle.className = "day-title";
  dayTitle.style.cssText = `position:absolute; top:${DAY_TITLE_Y}px; left:0; width:100%; text-align:center; font-family:Orbitron, sans-serif;`;
  dayTitle.innerHTML = `
    <span style="font-size:13px; font-weight:600; letter-spacing:0.55em; color:#888; display:block; margin-bottom:6px;">— THIRTIETH ANNIVERSARY —</span>
    <div class="name" style="font-size:92px; font-weight:900; letter-spacing:0.04em; color:#fff; text-shadow:0 0 30px ${EDC_MAGENTA}aa, 0 0 60px ${EDC_MAGENTA}55; line-height:1;">${DAY_LABELS[day]}</div>
    <span style="font-size:21px; font-weight:600; letter-spacing:0.4em; color:#fff; margin-top:8px; display:block;">${DAY_DATES[day]}</span>
    <span style="font-size:38px; font-weight:900; letter-spacing:0.06em; background:linear-gradient(90deg, ${EDC_CYAN}, ${EDC_MAGENTA}, ${EDC_YELLOW}); -webkit-background-clip:text; background-clip:text; color:transparent; text-shadow:0 0 24px ${EDC_MAGENTA}55; display:block; margin-top:14px; line-height:1;">★ 30 YEARS ★</span>
    <span style="font-size:11px; font-weight:700; letter-spacing:0.55em; color:${EDC_MAGENTA}; text-shadow:0 0 10px ${EDC_MAGENTA}66; display:block; margin-top:6px;">UNDER THE ELECTRIC SKY · 1997 — 2026</span>
  `;
  root.appendChild(dayTitle);

  // Empty stage list → show empty state and bail.
  if (stages.length === 0) {
    const empty = document.createElement("div");
    empty.style.cssText = `position:absolute; top:${GRID_TOP + gridH/2}px; left:0; width:100%; text-align:center; font-family:Orbitron,sans-serif; font-size:24px; font-weight:700; letter-spacing:0.2em; color:#666;`;
    empty.textContent = "PICK AT LEAST ONE STAGE";
    root.appendChild(empty);
    return root;
  }

  // Day's sets on selected stages.
  const dayPerfs = setsForDayOnStages(allSets, day, stages).filter(p => {
    const min = (p.startUTC.getTime() - new Date("2026-05-" + (14 + day) + "T19:00:00-07:00").getTime()) / 60000;
    return min >= 0;
  });

  const stageIndex = new Map(stages.map((s, i) => [s, i]));

  // Stage column headers
  stages.forEach((stage, i) => {
    const x = LEFT_MARGIN + i * (colW + COL_GAP);
    const c = STAGE_HEADER_WORDS[stage] ? "" : "";
    const color = (allSets.find(s => s.stage === stage)?.stageColor) || "#666";
    const words = STAGE_HEADER_WORDS[stage] ?? stage.split(" ");
    const head = document.createElement("div");
    head.className = "stage-head";
    head.style.cssText = `position:absolute; top:${GRID_TOP - STAGE_HEAD_HEIGHT - 6}px; left:${x}px; width:${colW}px; height:${STAGE_HEAD_HEIGHT}px; border-bottom:3px solid ${color}; color:${color}; text-align:center; font-family:Orbitron,sans-serif; font-weight:700; letter-spacing:0; display:flex; flex-direction:column; justify-content:flex-end; padding-bottom:4px; overflow:hidden;`;
    head.innerHTML = words
      .map(w => `<span class="stage-word" style="display:inline-block; align-self:center; width:max-content; line-height:1.0; text-transform:uppercase; white-space:nowrap; letter-spacing:-0.01em;">${w}</span>`)
      .join("");
    root.appendChild(head);
  });

  // Hour gridlines + labels
  for (let hour = 0; hour <= 10; hour++) {
    const m = hour * 60;
    if (m > GRID_END_MIN) break;
    const y = GRID_TOP + m * pxPerMin;
    const labelDate = new Date("2026-05-15T19:00:00-07:00");
    labelDate.setMinutes(labelDate.getMinutes() + m);
    const line = document.createElement("div");
    line.style.cssText = `position:absolute; left:${LEFT_MARGIN - 6}px; width:${W - LEFT_MARGIN - RIGHT_MARGIN + 6}px; top:${y}px; height:1px; background:rgba(255,255,255,0.05);`;
    root.appendChild(line);
    const label = document.createElement("div");
    label.style.cssText = `position:absolute; left:0; width:${LEFT_MARGIN - 8}px; top:${y - 16}px; text-align:right; padding-right:10px; font-family:Orbitron,sans-serif; font-size:26px; font-weight:800; color:#ccc; letter-spacing:0.04em; text-shadow:0 0 10px rgba(0,0,0,0.6);`;
    label.textContent = fmtAxisHour(labelDate);
    root.appendChild(label);
  }
  // 5:30 AM marker
  {
    const y = GRID_TOP + GRID_END_MIN * pxPerMin;
    const line = document.createElement("div");
    line.style.cssText = `position:absolute; left:${LEFT_MARGIN - 6}px; width:${W - LEFT_MARGIN - RIGHT_MARGIN + 6}px; top:${y}px; height:1px; background:rgba(255,255,255,0.05);`;
    root.appendChild(line);
    const label = document.createElement("div");
    label.style.cssText = `position:absolute; left:0; width:${LEFT_MARGIN - 8}px; top:${y - 16}px; text-align:right; padding-right:10px; font-family:Orbitron,sans-serif; font-size:26px; font-weight:800; color:#ccc; letter-spacing:0.04em;`;
    label.textContent = "5:30AM";
    root.appendChild(label);
  }

  // Fireworks: thin strip behind blocks + margin label.
  const fwY = GRID_TOP + FIREWORKS_START_MIN * pxPerMin;
  const fwH = (FIREWORKS_END_MIN - FIREWORKS_START_MIN) * pxPerMin;
  const fwStrip = document.createElement("div");
  fwStrip.style.cssText = `position:absolute; top:${fwY}px; height:${fwH}px; left:${LEFT_MARGIN}px; width:${gridInnerW}px; z-index:1; background:linear-gradient(90deg, ${EDC_MAGENTA}88 0%, ${EDC_YELLOW}cc 50%, ${EDC_CYAN}88 100%); box-shadow:0 0 16px ${EDC_YELLOW}aa, 0 0 32px ${EDC_MAGENTA}55; border-radius:3px; display:flex; align-items:center; justify-content:center; overflow:hidden;`;
  fwStrip.innerHTML = `<span style="font-family:Orbitron,sans-serif; font-size:14px; font-weight:900; letter-spacing:0.3em; color:#000; text-shadow:0 0 4px #fff; white-space:nowrap;">★ FIREWORKS · 1:42AM ★</span>`;
  root.appendChild(fwStrip);

  const fwMargin = document.createElement("div");
  fwMargin.style.cssText = `position:absolute; left:4px; width:${LEFT_MARGIN - 12}px; top:${fwY - 12}px; z-index:50; display:flex; align-items:center; gap:4px; pointer-events:none;`;
  fwMargin.innerHTML = `
    <span style="font-size:22px; color:${EDC_YELLOW}; text-shadow:0 0 6px ${EDC_MAGENTA}, 0 0 12px ${EDC_YELLOW}; line-height:1;">★</span>
    <span style="font-family:Orbitron,sans-serif; font-size:9px; font-weight:800; color:${EDC_MAGENTA}; letter-spacing:0.05em; line-height:1.1; text-shadow:0 0 6px ${EDC_MAGENTA}55;">FIREWORKS<br>1:42AM</span>
  `;
  root.appendChild(fwMargin);

  // Set blocks
  const gridZeroMs = new Date(`2026-05-${14 + day}T19:00:00-07:00`).getTime();
  for (const p of dayPerfs) {
    const idx = stageIndex.get(p.stage);
    if (idx === undefined) continue;
    const x = LEFT_MARGIN + idx * (colW + COL_GAP);
    const startMin = (p.startUTC.getTime() - gridZeroMs) / 60000;
    const y = GRID_TOP + startMin * pxPerMin;
    const blockH = Math.max(p.durationMin * pxPerMin - 2, 28);
    const base = p.stageColor;
    const bright = brighten(base, 1.25);
    const dark = brighten(base, 0.75);

    const isFav = faveIds.has(p.id);
    const labelClass = p.durationMin >= 70 ? "headliner" : p.durationMin >= 50 ? "main" : "support";
    const classes = ["set", labelClass];
    if (p.isCloser) classes.push("closer");
    if (isFav) classes.push("favorite");

    let glow: string;
    if (isFav && p.isCloser) {
      glow = `outline:3px solid ${FAVORITE_GOLD}; outline-offset:2px; box-shadow:0 0 22px rgba(251,191,36,0.7), inset 0 0 0 3px #fff, inset 0 0 0 5px ${EDC_MAGENTA};`;
    } else if (isFav) {
      glow = `outline:3px solid ${FAVORITE_GOLD}; outline-offset:2px; box-shadow:0 0 16px rgba(251,191,36,0.55), inset 0 0 0 1px rgba(251,191,36,0.35);`;
    } else if (p.isCloser) {
      glow = `box-shadow:0 0 32px ${EDC_MAGENTA}99, inset 0 0 0 3px #fff, inset 0 0 0 5px ${EDC_MAGENTA};`;
    } else if (labelClass === "headliner") {
      glow = `box-shadow:0 0 18px ${bright}66, inset 0 0 0 1.5px ${bright}cc;`;
    } else {
      glow = `box-shadow:inset 0 0 0 1px ${bright}66;`;
    }

    const dim = spotlightFaves && faveIds.size > 0 && !isFav ? "opacity:0.55;" : "";

    const block = document.createElement("div");
    block.className = classes.join(" ");
    block.style.cssText = `position:absolute; left:${x}px; top:${y}px; width:${colW}px; height:${blockH}px; background:linear-gradient(165deg, ${bright}, ${base} 50%, ${dark}); ${glow} ${dim} border-radius:7px; padding:5px 8px 4px; overflow:hidden; color:#fff; z-index:2;`;
    const badgeHTML = isFav
      ? `<div class="badge" style="position:absolute; bottom:4px; right:6px; font-family:Orbitron,sans-serif; font-size:8px; font-weight:700; background:${FAVORITE_GOLD}; color:#000; padding:2px 5px; border-radius:3px; letter-spacing:0.1em;">★ FAVE</div>`
      : p.isCloser && !p.badge
        ? `<div class="badge" style="position:absolute; bottom:4px; right:6px; font-family:Orbitron,sans-serif; font-size:8px; font-weight:700; background:${EDC_MAGENTA}; color:#fff; padding:2px 5px; border-radius:3px; letter-spacing:0.1em;">CLOSER</div>`
        : p.badge
          ? `<div class="badge" style="position:absolute; bottom:4px; right:6px; font-family:Orbitron,sans-serif; font-size:8px; font-weight:700; background:rgba(0,0,0,0.55); color:#fff; padding:2px 5px; border-radius:3px; letter-spacing:0.1em;">${p.badge}</div>`
          : "";

    block.innerHTML = `
      <div class="time" style="font-family:'JetBrains Mono', monospace; font-size:22px; font-weight:700; color:#fff; letter-spacing:0.01em; line-height:1.0; text-shadow:0 0 6px rgba(0,0,0,0.7), 0 1px 0 rgba(0,0,0,0.6); margin-bottom:2px;">${fmtBlockTime(p.startUTC)}</div>
      <div class="artist" style="font-family:Inter,sans-serif; line-height:1.0; margin-top:2px; color:#fff; text-shadow:0 1px 0 rgba(0,0,0,0.45); text-align:center; display:block; white-space:nowrap; word-break:normal;">${escapeHTML(p.artistDisplay)}</div>
      ${badgeHTML}
    `;
    root.appendChild(block);
  }

  // Bottom tagline (above home indicator)
  const tagline = document.createElement("div");
  tagline.style.cssText = `position:absolute; top:${TAGLINE_Y}px; left:0; width:100%; text-align:center; font-family:Orbitron,sans-serif; font-size:11px; letter-spacing:0.5em; color:#555; font-weight:600;`;
  tagline.textContent = "UNDER THE ELECTRIC SKY · 30TH ANNIVERSARY · LVMS";
  root.appendChild(tagline);

  // .word default styling (autofit overrides font-size per word).
  // width:max-content + margin auto so each word sizes to its natural text
  // width (so scrollWidth measurements work) AND centers under the column.
  const styleEl = document.createElement("style");
  styleEl.textContent = `.wp-root .artist .word { display:block; width:max-content; margin:0 auto; line-height:1.05; white-space:nowrap; letter-spacing:-0.01em; }`;
  root.appendChild(styleEl);

  return root;
}

function brighten(hex: string, amount: number): string {
  const h = hex.replace("#", "");
  const r = Math.min(255, Math.floor(parseInt(h.slice(0, 2), 16) * amount));
  const g = Math.min(255, Math.floor(parseInt(h.slice(2, 4), 16) * amount));
  const b = Math.min(255, Math.floor(parseInt(h.slice(4, 6), 16) * amount));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

function escapeHTML(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
